/**
 * Pure, dependency-free core for embedding an IPTC "AI provenance" marker into
 * an image. Operates on `Uint8Array` (Node's `Buffer` is one) and uses only
 * cross-environment built-ins (`TextEncoder`, `DataView`), so it is safe to
 * import from BOTH the backend and the browser bundle. See GH #118.
 *
 * Backend usage: `imageProvenance.ts` (raw generated/edited images).
 * Frontend usage: `frontend/src/lib/canvasProvenance.ts` (flattened canvases).
 */

/** IPTC DigitalSourceType controlled-vocabulary URIs. */
export const DIGITAL_SOURCE_TYPE = {
  /** Fully AI-generated media (a raw generation). */
  generated: 'https://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia',
  /** A composite that contains AI-generated elements (e.g. a canvas with overlays). */
  composite: 'https://cv.iptc.org/newscodes/digitalsourcetype/compositeWithTrainedAlgorithmicMedia',
} as const

export type DigitalSourceType = (typeof DIGITAL_SOURCE_TYPE)[keyof typeof DIGITAL_SOURCE_TYPE]

// XMP APP1 segment identifier for JPEG, including its null terminator.
const XMP_APP1_IDENTIFIER = 'http://ns.adobe.com/xap/1.0/\0'
// Keyword marking an iTXt chunk as the XMP packet in PNG.
const PNG_XMP_KEYWORD = 'XML:com.adobe.xmp'
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
// Substring shared by both DigitalSourceType values — used to detect an
// existing marker so we never double-stamp.
const EXISTING_MARKER = 'AlgorithmicMedia'

const encoder = new TextEncoder()

function ascii(text: string): Uint8Array {
  return encoder.encode(text)
}

function concat(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0)
  const out = new Uint8Array(total)
  let offset = 0
  for (const part of parts) {
    out.set(part, offset)
    offset += part.length
  }
  return out
}

function containsAscii(haystack: Uint8Array, needle: string): boolean {
  const bytes = ascii(needle)
  outer: for (let i = 0; i + bytes.length <= haystack.length; i++) {
    for (let j = 0; j < bytes.length; j++) {
      if (haystack[i + j] !== bytes[j]) continue outer
    }
    return true
  }
  return false
}

/** The XMP packet asserting AI provenance via IPTC DigitalSourceType. */
function buildXmpPacket(sourceType: string): string {
  // The xpacket header conventionally opens with a UTF-8 BOM; build it from an
  // escape so this source file contains no irregular whitespace.
  const bom = '\uFEFF'
  return (
    `<?xpacket begin="${bom}" id="W5M0MpCehiHzreSzNTczkc9d"?>` +
    `<x:xmpmeta xmlns:x="adobe:ns:meta/">` +
    `<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">` +
    `<rdf:Description rdf:about="" ` +
    `xmlns:Iptc4xmpExt="http://iptc.org/std/Iptc4xmpExt/2008-02-29/">` +
    `<Iptc4xmpExt:DigitalSourceType>${sourceType}</Iptc4xmpExt:DigitalSourceType>` +
    `</rdf:Description>` +
    `</rdf:RDF>` +
    `</x:xmpmeta>` +
    `<?xpacket end="w"?>`
  )
}

type ImageFormat = 'jpeg' | 'png' | 'unknown'

function detectFormat(bytes: Uint8Array): ImageFormat {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'jpeg'
  }
  if (bytes.length >= 8 && PNG_SIGNATURE.every((b, i) => bytes[i] === b)) {
    return 'png'
  }
  return 'unknown'
}

/** CRC-32 (ISO 3309 / PNG), table-free. */
function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff
  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i]!
    for (let j = 0; j < 8; j++) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1))
    }
  }
  return (crc ^ 0xffffffff) >>> 0
}

function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
}

/** Insert an XMP APP1 segment after the JPEG SOI marker (and any JFIF APP0). */
function injectJpegXmp(bytes: Uint8Array, xmp: Uint8Array): Uint8Array {
  const payload = concat([ascii(XMP_APP1_IDENTIFIER), xmp])

  // Segment length is a 2-byte big-endian count that includes the length bytes.
  const segmentLength = payload.length + 2
  if (segmentLength > 0xffff) {
    throw new Error(`XMP APP1 payload too large for a single JPEG segment: ${segmentLength}`)
  }

  const header = new Uint8Array(4)
  header[0] = 0xff
  header[1] = 0xe1
  view(header).setUint16(2, segmentLength, false)

  // Insert after SOI (FFD8). If a JFIF APP0 segment immediately follows SOI,
  // splice after it instead, to preserve the conventional JFIF segment ordering.
  let insertAt = 2
  if (bytes.length >= 6 && bytes[2] === 0xff && bytes[3] === 0xe0) {
    insertAt = 4 + view(bytes).getUint16(4, false) // APP0 length includes its 2 length bytes
  }

  return concat([bytes.subarray(0, insertAt), header, payload, bytes.subarray(insertAt)])
}

/** Insert an XMP iTXt chunk immediately after the PNG IHDR chunk. */
function injectPngXmp(bytes: Uint8Array, xmp: Uint8Array): Uint8Array {
  // PNG layout: 8-byte signature, then IHDR chunk.
  // Each chunk: length(4) + type(4) + data(length) + crc(4).
  const ihdrLength = view(bytes).getUint32(8, false)
  const insertAt = 8 + 4 + 4 + ihdrLength + 4 // end of IHDR chunk

  // iTXt data: keyword \0 compressionFlag compressionMethod \0(lang) \0(translated) text
  const data = concat([ascii(PNG_XMP_KEYWORD), new Uint8Array([0x00, 0x00, 0x00, 0x00, 0x00]), xmp])

  const type = ascii('iTXt')
  const length = new Uint8Array(4)
  view(length).setUint32(0, data.length, false)
  const crc = new Uint8Array(4)
  view(crc).setUint32(0, crc32(concat([type, data])), false)

  const chunk = concat([length, type, data, crc])
  return concat([bytes.subarray(0, insertAt), chunk, bytes.subarray(insertAt)])
}

/**
 * Returns a copy of `bytes` with the given IPTC DigitalSourceType embedded as
 * an XMP packet. Returns the input unchanged if it is already marked. Throws on
 * an unsupported image format — callers decide how to handle that.
 */
export function embedDigitalSourceType(bytes: Uint8Array, sourceType: string): Uint8Array {
  if (containsAscii(bytes, EXISTING_MARKER)) return bytes

  const xmp = encoder.encode(buildXmpPacket(sourceType))
  const format = detectFormat(bytes)

  if (format === 'jpeg') return injectJpegXmp(bytes, xmp)
  if (format === 'png') return injectPngXmp(bytes, xmp)

  throw new Error('Unsupported image format for AI provenance stamping')
}
